/**
 * PortalPreviewManager - Creates portal preview effect for navigation hotspots
 * Spawns inner skydomes that reveal destination scenes when hovering over teleport hotspots
 */

import { Logger } from '../utils/Logger';
import { Scene } from '@babylonjs/core/scene';
import type { Observer } from '@babylonjs/core/Misc/observable';

const logger = Logger.getLogger('PortalPreview');
import { Mesh } from '@babylonjs/core/Meshes/mesh';
import { TransformNode } from '@babylonjs/core/Meshes/transformNode';
import { CreateSphere } from '@babylonjs/core/Meshes/Builders/sphereBuilder';
import { ShaderMaterial } from '@babylonjs/core/Materials/shaderMaterial';
import { Texture } from '@babylonjs/core/Materials/Textures/texture';
import { VideoTexture } from '@babylonjs/core/Materials/Textures/videoTexture';
import { Vector3 } from '@babylonjs/core/Maths/math.vector';
import { Effect } from '@babylonjs/core/Materials/effect';
import { CreatePlane } from '@babylonjs/core/Meshes/Builders/planeBuilder';
import { AdvancedDynamicTexture } from '@babylonjs/gui/2D/advancedDynamicTexture';
import { Rectangle } from '@babylonjs/gui/2D/controls/rectangle';
import { TextBlock } from '@babylonjs/gui/2D/controls/textBlock';
import { Control } from '@babylonjs/gui/2D/controls/control';

import type { HotspotConfig, ExperienceStage } from '../ui/UIManager';
import type { PortalEffectStyle } from '../types/config';
import type { SceneManager } from './SceneManager';

// Register custom shader
Effect.ShadersStore["portalPreviewVertexShader"] = `
precision highp float;

// Attributes
attribute vec3 position;
attribute vec3 normal;
attribute vec2 uv;

// Uniforms
uniform mat4 worldViewProjection;
uniform mat4 world;

// Varying
varying vec2 vUV;
varying vec3 vWorldPosition;

void main(void) {
    gl_Position = worldViewProjection * vec4(position, 1.0);
    vUV = uv;
    vWorldPosition = (world * vec4(position, 1.0)).xyz;
}
`;

Effect.ShadersStore["portalPreviewFragmentShader"] = `
precision highp float;

// Varying
varying vec2 vUV;
varying vec3 vWorldPosition;

// Uniforms
uniform sampler2D textureSampler;
uniform vec3 hotspotPosition;
uniform float revealRadius;
uniform float edgeSoftness;
uniform float isActive;
uniform float useTexture;  // 1.0 = use texture, 0.0 = use solid color
uniform vec3 solidColor;   // Solid color for color-only skyboxes
uniform float time;        // Time for animation
uniform float transitionProgress; // 0 to 1, expands portal for scene transition
uniform float basePortalSize; // Starting portal size (0.99-0.995), larger = more visible initially
uniform float effectStyle; // 0=clean, 1=enhanced, 2=minimal, 3=alien
uniform vec3 glowColor;    // User-customizable glow color
uniform float edgeOpacity; // Leading edge glow opacity (0 = transparent, 1 = fully visible)

void main(void) {
    // Calculate direction from origin to fragment (normalized)
    vec3 fragmentDir = normalize(vWorldPosition);

    // Calculate direction from origin to hotspot (normalized)
    vec3 hotspotDir = normalize(hotspotPosition);

    // Calculate angular similarity using dot product
    float dotProduct = dot(fragmentDir, hotspotDir);

    // Calculate angle around the hotspot for circular wave
    float angle = atan(vWorldPosition.y - hotspotPosition.y, vWorldPosition.x - hotspotPosition.x);

    // Custom easing: Slow start (0-20%), then accelerate
    float easedProgress = transitionProgress;
    if (transitionProgress < 0.2) {
        // First 20%: Cubic ease in (slower)
        easedProgress = (transitionProgress / 0.2) * (transitionProgress / 0.2) * (transitionProgress / 0.2) * 0.2;
    } else {
        // After 20%: Exponential acceleration
        float t = (transitionProgress - 0.2) / 0.8;
        easedProgress = 0.2 + (t * t * 2.5) * 0.8;
        easedProgress = min(easedProgress, 1.0);
    }

    // Base portal threshold - can expand during transition
    // easedProgress: 0 = basePortalSize (small portal), 1 = full sphere (-1.0)
    float baseThreshold = mix(basePortalSize, -1.0, easedProgress);

    // Portal edge effects - vary by style
    float portalSizeFactor = (basePortalSize - 0.995) / (0.9996 - 0.995); // 0 = full size, 1 = tiny

    // Style-dependent shimmer amount and wave calculations
    float shimmerAmount;
    float edgeWave = 0.0;
    float edgePulse = 0.0;

    if (effectStyle < 0.5) {
        // Style 0: CLEAN - Original simple effect
        shimmerAmount = mix(0.002, 0.008, portalSizeFactor);
        float wave1 = sin(angle * 12.0 + time * 2.0) * shimmerAmount;
        float wave2 = sin(angle * 9.0 - time * 1.5) * shimmerAmount * 0.75;
        edgeWave = wave1 + wave2;
        edgePulse = 0.0; // No pulse in clean mode
    } else if (effectStyle < 1.5) {
        // Style 1: ENHANCED - Multi-layered with pulse
        shimmerAmount = mix(0.003, 0.012, portalSizeFactor);
        float wave1 = sin(angle * 14.0 + time * 3.0) * shimmerAmount;
        float wave2 = sin(angle * 10.0 - time * 2.2) * shimmerAmount * 0.8;
        float wave3 = sin(angle * 18.0 + time * 1.8) * shimmerAmount * 0.6;
        float wave4 = cos(angle * 7.0 - time * 2.5) * shimmerAmount * 0.5;
        edgeWave = wave1 + wave2 + wave3 + wave4;
        float pulse = sin(time * 2.5) * 0.5 + 0.5;
        edgePulse = pulse * shimmerAmount * 0.3;
    } else if (effectStyle < 2.5) {
        // Style 2: MINIMAL - Subtle, almost no effects
        shimmerAmount = mix(0.001, 0.003, portalSizeFactor);
        edgeWave = sin(angle * 8.0 + time * 1.0) * shimmerAmount;
        edgePulse = 0.0;
    } else {
        // Style 3: ENERGETIC - Electric/Lightning style with chaotic movement
        shimmerAmount = mix(0.008, 0.020, portalSizeFactor);

        // Chaotic overlapping waves with prime numbers for organic feel
        float wave1 = sin(angle * 23.0 + time * 5.2) * shimmerAmount;
        float wave2 = cos(angle * 17.0 - time * 4.7) * shimmerAmount * 1.2;
        float wave3 = sin(angle * 31.0 + time * 6.1) * shimmerAmount * 0.9;

        // Add sharp "electrical" noise
        float noise = fract(sin(angle * 100.0 + time * 7.0) * 43758.5453);
        float electricJitter = (noise - 0.5) * shimmerAmount * 2.0;

        edgeWave = wave1 + wave2 + wave3 + electricJitter;

        // Rapid aggressive pulse
        float pulse = sin(time * 5.0) * 0.5 + 0.5;
        edgePulse = pulse * shimmerAmount * 0.6;
    }

    // Shared constants for edge calculations
    float baseEdgeSize = 0.0005;

    // Expand the edge influence zone during transition
    float edgeZoneSize = mix(baseEdgeSize, 2.0, easedProgress); // Use eased progress

    // 1. Calculate base influence for wave amplitude (how close to average edge)
    float baseDist = abs(dotProduct - baseThreshold);
    float waveAmpInfluence = smoothstep(edgeZoneSize, 0.0, baseDist);

    // Square the influence to make it fall off even faster
    waveAmpInfluence = mix(waveAmpInfluence * waveAmpInfluence, waveAmpInfluence, easedProgress);

    // 2. Calculate the actual wavy edge position with pulse
    float angleThreshold = baseThreshold + (edgePulse * waveAmpInfluence * (1.0 - easedProgress));
    float angleSoftness = 0.002;

    // Alpha for the portal content (texture/color)
    // 1.0 inside, 0.0 outside
    float contentAlpha = smoothstep(angleThreshold - angleSoftness, angleThreshold, dotProduct);
    contentAlpha *= isActive;

    // Choose between texture and solid color
    vec3 contentColor;
    if (useTexture > 0.5) {
        vec4 textureColor = texture2D(textureSampler, vUV);
        contentColor = textureColor.rgb;
    } else {
        contentColor = solidColor;
    }

    // Style-dependent glow effects
    float signedDist = angleThreshold - dotProduct;

    float outerGlow;
    float innerGlow = 0.0;
    float sparkle = 0.0;
    vec3 portalGlowColor;  // Local variable for computed glow color
    float glowStrength;

    if (effectStyle < 0.5) {
        // Style 0: CLEAN - Simple glow using user color
        float glowWidth = baseEdgeSize * 8.0;
        outerGlow = smoothstep(glowWidth, 0.0, signedDist) * step(0.0, signedDist);
        glowStrength = mix(1.5, 2.0, portalSizeFactor);
        portalGlowColor = glowColor; // Use uniform glow color

    } else if (effectStyle < 1.5) {
        // Style 1: ENHANCED - Multi-layer with color shift using user color
        float glowWidth = baseEdgeSize * 12.0;
        outerGlow = smoothstep(glowWidth, 0.0, signedDist) * step(0.0, signedDist);
        float innerGlowWidth = baseEdgeSize * 4.0;
        innerGlow = smoothstep(innerGlowWidth, 0.0, abs(signedDist)) * 1.5;
        glowStrength = mix(2.0, 2.5, portalSizeFactor);

        // Dynamic color shifting between user color and complement
        float colorShift = sin(angle * 3.0 + time * 1.5) * 0.5 + 0.5;
        vec3 complementColor = vec3(1.0) - glowColor * 0.5 + glowColor * 0.5; // Subtle complement
        vec3 brightColor = glowColor * 1.3;
        portalGlowColor = mix(glowColor, brightColor, colorShift);

        // Sparkles
        float pulse = sin(time * 2.5) * 0.5 + 0.5;
        sparkle = fract(sin(angle * 50.0 + time * 3.0) * 43758.5453);
        sparkle = step(0.95, sparkle) * outerGlow * pulse * 0.5;

    } else if (effectStyle < 2.5) {
        // Style 2: MINIMAL - Very subtle using user color
        float glowWidth = baseEdgeSize * 5.0;
        outerGlow = smoothstep(glowWidth, 0.0, signedDist) * step(0.0, signedDist);
        glowStrength = mix(1.0, 1.3, portalSizeFactor);
        portalGlowColor = mix(glowColor, vec3(1.0), 0.3); // Slightly desaturated

    } else {
        // Style 3: ALIEN - Otherworldly organic portal with tendrils and membrane

        // === ORGANIC TENDRIL EFFECT ===
        // Create multiple wavy tendrils extending from the portal edge
        float tendrilBase = 0.0;
        for (float i = 0.0; i < 6.0; i++) {
            // Each tendril has unique phase and frequency
            float tendrilAngle = angle + i * 1.047; // 60 degrees apart
            float tendrilWave = sin(tendrilAngle * 3.0 + time * (1.5 + i * 0.2)) * 0.5 + 0.5;
            float tendrilPulse = sin(time * (2.0 + i * 0.3) + i) * 0.3 + 0.7;

            // Tendril extends outward with organic waviness
            float tendrilLength = baseEdgeSize * (8.0 + tendrilWave * 12.0) * tendrilPulse;
            float tendrilWidth = baseEdgeSize * (2.0 + sin(time * 3.0 + i) * 0.5);

            // Distance to this tendril
            float tendrilDist = smoothstep(tendrilLength, 0.0, signedDist) *
                               smoothstep(tendrilWidth, 0.0, abs(sin(tendrilAngle - angle) * signedDist));
            tendrilBase += tendrilDist * 0.3;
        }

        // Main glow with organic irregularity
        float organicWobble = sin(angle * 7.0 + time * 1.5) * 0.3 + sin(angle * 13.0 - time * 2.0) * 0.2;
        float glowWidth = baseEdgeSize * (12.0 + organicWobble * 5.0);
        outerGlow = smoothstep(glowWidth, 0.0, signedDist) * step(0.0, signedDist);
        outerGlow = max(outerGlow, tendrilBase);

        // === MEMBRANE PULSE ===
        // Breathing membrane that contracts and expands
        float breathe = sin(time * 0.8) * 0.5 + 0.5;
        float heartbeat = pow(sin(time * 1.5) * 0.5 + 0.5, 3.0); // Sharp heartbeat pulse
        float membrane = breathe * 0.7 + heartbeat * 0.3;

        float innerGlowWidth = baseEdgeSize * (2.0 + membrane * 4.0);
        innerGlow = smoothstep(innerGlowWidth, 0.0, abs(signedDist)) * (1.5 + membrane);

        glowStrength = mix(3.0, 4.5, portalSizeFactor);

        // === ALIEN COLOR PALETTE ===
        // Shifting between bioluminescent colors
        float colorPhase1 = sin(time * 0.5 + angle * 2.0) * 0.5 + 0.5;
        float colorPhase2 = sin(time * 0.7 - angle * 3.0) * 0.5 + 0.5;

        vec3 bioGreen = vec3(0.1, 0.9, 0.4);      // Bioluminescent green
        vec3 voidPurple = vec3(0.5, 0.0, 0.8);    // Deep void purple
        vec3 cosmicBlue = vec3(0.0, 0.4, 0.9);    // Cosmic blue
        vec3 fleshPink = vec3(0.9, 0.3, 0.5);     // Organic pink

        // Complex color mixing
        vec3 alienColor1 = mix(bioGreen, voidPurple, colorPhase1);
        vec3 alienColor2 = mix(cosmicBlue, fleshPink, colorPhase2);
        vec3 alienColor = mix(alienColor1, alienColor2, sin(time * 0.3) * 0.5 + 0.5);

        // Blend with user's chosen color (40% user, 60% alien)
        portalGlowColor = mix(glowColor, alienColor, 0.6);

        // === BIOLUMINESCENT VEINS ===
        // Pulsing vein patterns
        float veinPattern = sin(angle * 20.0 + time * 2.0) * sin(angle * 15.0 - time * 1.5);
        veinPattern = smoothstep(0.7, 1.0, veinPattern);
        float veinGlow = veinPattern * outerGlow * (0.5 + heartbeat * 0.5);
        portalGlowColor += vec3(0.3, 1.0, 0.5) * veinGlow * 0.4;

        // === FLOATING SPORES/PARTICLES ===
        float sporeTime = time * 0.8;
        float spore1 = step(0.93, fract(sin(angle * 41.0 + sporeTime) * 43758.5453));
        float spore2 = step(0.95, fract(sin(angle * 73.0 - sporeTime * 1.3) * 12345.6789));
        float spore3 = step(0.97, fract(sin(angle * 97.0 + sporeTime * 0.7) * 98765.4321));
        float spores = (spore1 + spore2 * 0.8 + spore3 * 0.6) * outerGlow;

        // Spores drift and pulse
        float sporePulse = sin(time * 4.0 + angle * 10.0) * 0.5 + 0.5;
        sparkle = spores * (0.6 + sporePulse * 0.4);

        // === DIMENSIONAL RIFT EFFECT ===
        // Occasional "reality tear" flashes
        float riftChance = fract(sin(time * 0.2 + angle * 1.5) * 99999.0);
        float rift = step(0.985, riftChance) * (sin(time * 20.0) * 0.5 + 0.5);
        sparkle += rift * innerGlow * 1.5;

        // Add eerie eye-like glow in center
        float eyeGlow = smoothstep(0.02, 0.0, abs(signedDist + baseEdgeSize * 2.0));
        eyeGlow *= sin(time * 1.0) * 0.3 + 0.7;
        portalGlowColor += vec3(1.0, 0.8, 0.9) * eyeGlow * 0.3;
    }

    // Combine glows
    float combinedGlow = max(outerGlow, innerGlow);

    // Apply glow fade during transition
    float glowFade = smoothstep(0.1, 0.0, easedProgress);
    float finalGlowAlpha = (combinedGlow * glowStrength + sparkle) * glowFade * isActive * edgeOpacity;

    // Combine Alphas
    float finalAlpha = max(contentAlpha, finalGlowAlpha);

    // Combine Colors - when edgeOpacity is 0, blend directly to content (no glow color)
    // This ensures clean transitions with no colored edge when opacity is 0
    vec3 effectiveGlowColor = mix(contentColor, portalGlowColor, edgeOpacity);
    vec3 finalColor = mix(effectiveGlowColor, contentColor, contentAlpha);
    // Add sparkle highlights (enhanced/energetic styles only) - also respect edgeOpacity
    if (effectStyle > 0.5) {
        finalColor += vec3(1.0) * sparkle * edgeOpacity;
    }

    // Apply alpha
    gl_FragColor = vec4(finalColor, finalAlpha);
}
`;

// Re-export PortalEffectStyle from types for backwards compatibility
export type { PortalEffectStyle } from '../types/config';

interface PreviewDome {
    mesh: Mesh;
    material: ShaderMaterial;
    texture: Texture | VideoTexture | null;
    targetStageId: string;
    hotspotPosition: Vector3;
    hotspotConfig: HotspotConfig | null; // Reference to original hotspot config for per-portal settings (null for center transitions)
    currentAlpha: number;      // Current animated alpha value (0-1)
    targetAlpha: number;       // Target alpha value (0-1)
    currentPortalSize: number; // Current portal window size (0.9995-0.995)
    targetPortalSize: number;  // Target portal window size
    restSize: number;          // Portal-specific rest size
    hoverSize: number;         // Portal-specific hover size
    useSolidColor: boolean;    // True when using solid color fallback (no texture)
    isTransitioning: boolean;  // True when expanding for scene transition
    transitionProgress: number; // 0 to 1.2, how far through the transition
    transitionStartTime: number; // performance.now() when transition started
    labelMesh?: Mesh;           // Floating label above the portal
    labelTexture?: AdvancedDynamicTexture; // GUI texture for the label
}

export class PortalPreviewManager {
    private scene: Scene;
    private sceneManager: SceneManager;
    private portalEffectStyle: PortalEffectStyle = 'clean'; // Default to clean

    // User-friendly size scales (1.0 = default, 2.0 = 2x larger, 0.5 = half size)
    private portalRestSizeScale: number = 1.0;   // Rest size scale
    private portalHoverSizeScale: number = 1.0;  // Hover size scale
    private portalEdgeOpacity: number = 1.0;     // Leading edge glow opacity (0 = transparent, 1 = visible)
    private portalGlowColor: string = '#00bfff'; // Default glow color (cyan) for center transitions

    // Internal shader values (converted from scale)
    private portalRestSize: number = 0.9996;  // Portal size at rest (0.9995-0.9999, higher = smaller)
    private portalHoverSize: number = 0.995;  // Portal size on hover (0.995-0.9995, lower = larger)
    private previewDomes: Map<string, PreviewDome> = new Map(); // Key: hotspot ID
    private gazePortalDome: PreviewDome | null = null; // Special gaze-following portal
    private allStages: ExperienceStage[] = [];
    // @ts-expect-error - Intentionally kept alive: holds reference to scene observer for animation loop
    private _animationObserver: Observer<Scene> | null = null;
    public onTransitionComplete?: (targetStageId: string) => void; // Callback when transition animation finishes
    private lastCenterTransitionId: string | null = null; // Track the most recent center transition ID for texture transfer
    private parentNode: TransformNode | null = null; // Parent node for scene rotation grouping

    // Configuration
    private readonly DOME_RADIUS = 800; // Inner dome radius (outer is 1000)
    private readonly REVEAL_RADIUS = 300; // Radius around hotspot to reveal (larger for visibility)
    private readonly EDGE_SOFTNESS = 100; // Soft edge transition
    // Animation configuration
    private readonly ANIMATION_SPEED = 9; // Lerp speed per second for fade in/out
    private readonly TRANSITION_DURATION = 1.5; // Total transition duration in seconds

    constructor(scene: Scene, sceneManager: SceneManager) {
        this.scene = scene;
        this.sceneManager = sceneManager;
        this.setupAnimation();
    }

    /**
     * Set parent node for scene rotation grouping
     * Portal preview domes will be parented to this node so they rotate with the scene
     */
    setParentNode(parent: TransformNode | null): void {
        this.parentNode = parent;
    }

    /**
     * Setup animation loop for smooth fade in/out and edge wavering
     * Uses delta time for frame-rate independent animations
     */
    private setupAnimation(): void {
        logger.debug('Setting up animation observer');
        const startTime = performance.now();

        this._animationObserver = this.scene.onBeforeRenderObservable.add(() => {
            // Calculate elapsed time in seconds for shader animation
            const currentTime = (performance.now() - startTime) / 1000;

            // Get delta time in seconds for frame-rate independent animation
            const deltaTime = this.scene.getEngine().getDeltaTime() / 1000;

            this.previewDomes.forEach((preview) => {
                // Handle transition animation (portal expansion)
                if (preview.isTransitioning) {
                    // 1. Handle Fade In (if needed) - frame-rate independent lerp
                    const alphaDelta = preview.targetAlpha - preview.currentAlpha;
                    if (Math.abs(alphaDelta) > 0.001) {
                        preview.currentAlpha += alphaDelta * this.ANIMATION_SPEED * deltaTime;
                        preview.material.setFloat("isActive", preview.currentAlpha);
                    } else {
                        preview.currentAlpha = preview.targetAlpha;
                        preview.material.setFloat("isActive", preview.currentAlpha);
                    }

                    // 2. Handle Expansion using TIME-BASED easing (not incremental)
                    // This computes progress directly from elapsed time for perfectly smooth animation
                    const elapsedTime = (performance.now() - preview.transitionStartTime) / 1000;
                    const t = Math.min(elapsedTime / this.TRANSITION_DURATION, 1.0); // Normalized time 0-1

                    // Ease-in-out sine: smooth start AND smooth end
                    // Formula: (1 - cos(t * π)) / 2
                    const easedT = (1 - Math.cos(t * Math.PI)) / 2;

                    // Map eased time to progress (0 to 1.2, with extra 0.2 for hold)
                    preview.transitionProgress = easedT * 1.2;

                    // Transition complete when time is up
                    if (t >= 1.0) {
                        preview.transitionProgress = 1.2;
                        preview.isTransitioning = false; // Stop animating

                        // Transition complete - trigger callback ONCE
                        if (this.onTransitionComplete) {
                            logger.debug('Portal fully expanded, triggering scene load');
                            const callback = this.onTransitionComplete;
                            this.onTransitionComplete = undefined; // Clear before calling to prevent re-entry
                            callback(preview.targetStageId);
                        }
                    }

                    // Clamp display progress to 1.0 (don't over-expand in shader)
                    preview.material.setFloat("transitionProgress", Math.min(preview.transitionProgress, 1.0));
                } else {
                    // Normal hover animation - animate portal size, not alpha
                    // Frame-rate independent lerp
                    const sizeDelta = preview.targetPortalSize - preview.currentPortalSize;

                    if (Math.abs(sizeDelta) > 0.0001) {
                        // Animate portal size smoothly with deltaTime
                        preview.currentPortalSize += sizeDelta * this.ANIMATION_SPEED * deltaTime;
                    } else {
                        // Snap to target when very close
                        preview.currentPortalSize = preview.targetPortalSize;
                    }

                    // Update shader with animated portal size
                    preview.material.setFloat("basePortalSize", preview.currentPortalSize);
                    preview.material.setFloat("isActive", 1); // Always fully visible
                }

                // Update time for edge animation
                preview.material.setFloat("time", currentTime);
            });

            // Update gaze portal position to follow camera
            this.updateGazePortalPosition();
        });
    }

    /**
     * Initialize portal previews for navigation hotspots
     * @param hotspots All hotspots in the current stage
     * @param allStages All stages in the experience (to find target skyboxes)
     */
    async initialize(hotspots: HotspotConfig[], allStages: ExperienceStage[]): Promise<void> {
        logger.debug('Initializing portal previews...');
        this.allStages = allStages;

        // Clear existing previews
        this.dispose();

        // Filter for navigation hotspots only (exclude floor-arrow style - handled by FloorArrowManager)
        const navHotspots = hotspots.filter(h =>
            h.type === 'navigation' &&
            h.targetStageId &&
            h.portalEffectStyle !== 'floor-arrow'
        );

        logger.debug(`Found ${navHotspots.length} navigation hotspots`);

        // Create preview dome for each navigation hotspot
        for (const hotspot of navHotspots) {
            await this.createPreviewDome(hotspot);
        }

        logger.debug('All portal previews created');
    }

    /**
     * Create a preview dome for a specific navigation hotspot
     */
    private async createPreviewDome(hotspot: HotspotConfig): Promise<void> {
        if (!hotspot.targetStageId) return;

        logger.debug(`Creating preview dome for hotspot ${hotspot.id} -> stage ${hotspot.targetStageId}`);

        // Find target stage
        const targetStage = this.allStages.find(s => s.id === hotspot.targetStageId);
        if (!targetStage || !targetStage.skybox) {
            logger.warn(`No skybox found for target stage ${hotspot.targetStageId}`);
            return;
        }

        // Create inner sphere mesh
        const dome = CreateSphere(
            `portalPreview_${hotspot.id}`,
            {
                diameter: this.DOME_RADIUS * 2,
                segments: 64,
                sideOrientation: Mesh.BACKSIDE,
            },
            this.scene
        );

        dome.infiniteDistance = true;

        // Match main dome rotation exactly (DomeComponent.ts:74)
        dome.rotation.y = Math.PI;
        // NO X rotation - main dome doesn't have it, so portal previews shouldn't either

        // Apply target stage's skybox rotation if configured
        if (targetStage.skybox.rotation !== undefined) {
            const targetRotationRadians = (targetStage.skybox.rotation * Math.PI) / 180;
            dome.rotation.y += targetRotationRadians;
            logger.debug(`Applied target rotation ${targetStage.skybox.rotation}° to preview dome`);
        }

        // Render AFTER the main skybox (group 1 vs group 0)
        dome.renderingGroupId = 1;

        // Disable picking so it doesn't block hotspot interactions
        dome.isPickable = false;

        // Parent to scene content parent for rotation grouping (so portal POSITION rotates with scene)
        // But counter-rotate to cancel parent's rotation so portal CONTENT shows at target's rotation
        if (this.parentNode) {
            dome.parent = this.parentNode;
            // Counter-rotate to cancel parent's Y rotation - portal content should show target scene orientation
            dome.rotation.y -= this.parentNode.rotation.y;
            logger.debug(`Counter-rotated dome by ${(-this.parentNode.rotation.y * 180 / Math.PI).toFixed(1)}° to cancel parent rotation`);
        }

        // CRITICAL: Hide dome IMMEDIATELY after creation to prevent white flash
        dome.visibility = 0; // Fully transparent until we're ready to show it

        // Create custom shader material
        const material = new ShaderMaterial(
            `portalPreviewMaterial_${hotspot.id}`,
            this.scene,
            {
                vertex: "portalPreview",
                fragment: "portalPreview",
            },
            {
                attributes: ["position", "normal", "uv"],
                uniforms: [
                    "world",
                    "worldViewProjection",
                    "hotspotPosition",
                    "revealRadius",
                    "edgeSoftness",
                    "isActive",
                    "useTexture",
                    "solidColor",
                    "time",
                    "transitionProgress",
                    "basePortalSize",
                    "effectStyle",
                    "glowColor",
                    "edgeOpacity"
                ],
                samplers: ["textureSampler"],
            }
        );

        // Set initial uniforms
        // Transform hotspot position to world space if parent node is set (for scene rotation)
        let hotspotPos = new Vector3(hotspot.position.x, hotspot.position.y, hotspot.position.z);
        if (this.parentNode) {
            // Transform local position to world space using parent's world matrix
            hotspotPos = Vector3.TransformCoordinates(hotspotPos, this.parentNode.getWorldMatrix());
        }
        material.setVector3("hotspotPosition", hotspotPos);
        material.setFloat("revealRadius", this.REVEAL_RADIUS);
        material.setFloat("edgeSoftness", this.EDGE_SOFTNESS);
        material.setFloat("isActive", 0); // Hidden by default
        material.setFloat("time", 0); // Initialize time
        material.setFloat("transitionProgress", 0); // No transition by default

        // Use per-portal settings if available, fallback to defaults
        const portalRestSize = hotspot.portalRestSize !== undefined
            ? this.scaleToThreshold(hotspot.portalRestSize, 0.9996)
            : this.portalRestSize;
        const portalEffectStyle = hotspot.portalEffectStyle || this.portalEffectStyle;

        material.setFloat("basePortalSize", portalRestSize);
        material.setFloat("effectStyle", this.getStyleValue(portalEffectStyle));

        // Set glow color from hotspot config, fall back to global setting
        const portalColor = hotspot.portalColor || this.portalGlowColor;
        const glowColorVec = this.parseColor(portalColor);
        material.setVector3("glowColor", glowColorVec);

        // Set edge opacity from hotspot config, fall back to global setting
        const edgeOpacity = hotspot.portalEdgeOpacity !== undefined ? hotspot.portalEdgeOpacity : this.portalEdgeOpacity;
        material.setFloat("edgeOpacity", edgeOpacity);

        // Enable proper alpha blending for transparency
        material.transparencyMode = 3; // MATERIAL_ALPHABLEND
        material.backFaceCulling = false;
        material.disableDepthWrite = true; // Don't write to depth buffer for proper transparency

        // Load texture for target stage
        let texture: Texture | VideoTexture | null = null;
        const skyboxConfig = targetStage.skybox;

        if (skyboxConfig.type === 'color') {
            // Solid color skybox
            const colorHex = skyboxConfig.color || '#000000';
            const color = this.parseColor(colorHex);
            material.setVector3("solidColor", color);
            material.setFloat("useTexture", 0); // Don't use texture
            logger.debug(`Using solid color ${colorHex} for ${hotspot.id}`);
        } else if (skyboxConfig.type === 'image') {
            // Check Shared Cache First
            const cachedTexture = this.sceneManager.getTextureFromCache(targetStage.id);
            if (cachedTexture && cachedTexture instanceof Texture) {
                logger.debug(`Cache hit for ${hotspot.id}`);
                texture = cachedTexture;
                material.setTexture("textureSampler", texture);
                material.setFloat("useTexture", 1);
            } else {
                // Use DomeComponent's loadInBackground which properly handles multi-variant textures
                try {
                    const mediaItem = {
                        id: `${hotspot.id}-skybox`,
                        title: targetStage.id,
                        url: skyboxConfig.url || '' // MediaLoader will use variants if available
                    };

                    const domeComponent = this.sceneManager.getDomeComponent();
                    texture = await domeComponent.loadInBackground(
                        mediaItem,
                        'image',
                        undefined, // No progress callback
                        skyboxConfig // Pass full skybox config for variant selection
                    );

                    logger.debug(`Hotspot image texture loaded for ${hotspot.id}`);

                    material.setTexture("textureSampler", texture);
                    material.setFloat("useTexture", 1);

                    // Add to shared cache
                    if (texture) {
                        this.sceneManager.addTextureToCache(targetStage.id, texture, 'image');
                    }
                } catch (error) {
                    logger.error(`Hotspot image failed to load for ${hotspot.id}:`, error);
                    // Fall back to solid color
                    const color = this.parseColor('#000000');
                    material.setVector3("solidColor", color);
                    material.setFloat("useTexture", 0);
                }
            }
        } else if (skyboxConfig.type === 'video') {
            // For video skyboxes, use thumbnail if available (better performance)
            if (skyboxConfig.thumbnailUrl) {
                logger.debug(`Using video thumbnail for ${hotspot.id}:`, skyboxConfig.thumbnailUrl);
                try {
                    const domeComponent = this.sceneManager.getDomeComponent();
                    const mediaItem = {
                        id: `${hotspot.id}-thumbnail`,
                        title: targetStage.id,
                        url: skyboxConfig.thumbnailUrl
                    };
                    texture = await domeComponent.loadInBackground(mediaItem, 'image');

                    material.setTexture("textureSampler", texture);
                    material.setFloat("useTexture", 1);
                    logger.debug(`Video thumbnail texture set for ${hotspot.id}`);
                } catch (error) {
                    logger.error(`Thumbnail load failed for ${hotspot.id}, falling back to black`);
                    const color = this.parseColor('#000000');
                    material.setVector3("solidColor", color);
                    material.setFloat("useTexture", 0);
                }
            } else {
                // No thumbnail - show black (videos are too heavy for portal previews)
                logger.debug(`No thumbnail for video, using black for ${hotspot.id}`);
                const color = this.parseColor('#000000');
                material.setVector3("solidColor", color);
                material.setFloat("useTexture", 0);
            }
        }

        dome.material = material;

        // Hide dome initially to prevent white flash during texture load
        // dome.visibility = 0; // Already set above

        // Create label mesh (plane)
        const labelPlane = CreatePlane(`portalLabel_${hotspot.id}`, {
            width: 100,
            height: 35,
            sideOrientation: Mesh.DOUBLESIDE 
        }, this.scene);

        // Position label exactly at the hotspot center
        labelPlane.position = hotspotPos.clone(); 
        labelPlane.billboardMode = Mesh.BILLBOARDMODE_ALL; // Always face camera
        labelPlane.renderingGroupId = 2; // Render ON TOP of portal (group 1)
        labelPlane.visibility = 0; // Hidden by default
        labelPlane.isPickable = false; // Don't block rays

        // Create UI texture for label (High res for large size)
        const labelTexture = AdvancedDynamicTexture.CreateForMesh(labelPlane, 2048, 716);
        
        const labelBg = new Rectangle("labelBg");
        labelBg.width = 1;
        labelBg.height = 1;
        labelBg.cornerRadius = 0;
        labelBg.color = "transparent";
        labelBg.thickness = 0;
        labelBg.background = "transparent";
        labelTexture.addControl(labelBg);
        
        const labelText = new TextBlock("labelText");
        labelText.text = targetStage.name || "Scene";
        labelText.color = "white";
        labelText.alpha = 0.7;
        labelText.fontSize = 225;
        labelText.fontWeight = "bold";
        labelText.textHorizontalAlignment = Control.HORIZONTAL_ALIGNMENT_CENTER;
        labelText.textVerticalAlignment = Control.VERTICAL_ALIGNMENT_CENTER;
        labelBg.addControl(labelText);

        logger.debug('Dome setup complete:', {
            renderingGroupId: dome.renderingGroupId,
            transparencyMode: material.transparencyMode,
            disableDepthWrite: material.disableDepthWrite,
            backFaceCulling: material.backFaceCulling
        });

        // Calculate portal-specific sizes
        const portalHoverSize = hotspot.portalHoverSize !== undefined
            ? this.scaleToThreshold(hotspot.portalHoverSize, 0.995)
            : this.portalHoverSize;

        // Store preview dome
        this.previewDomes.set(hotspot.id, {
            mesh: dome,
            material: material,
            texture: texture,
            targetStageId: hotspot.targetStageId!,
            hotspotPosition: hotspotPos,
            hotspotConfig: hotspot, // Store reference for per-portal settings
            currentAlpha: 1,  // Always fully visible
            targetAlpha: 1,
            currentPortalSize: portalRestSize,  // Start at portal-specific rest size
            targetPortalSize: portalRestSize,   // Stay at rest size
            restSize: portalRestSize,            // Store for later use
            hoverSize: portalHoverSize,          // Store for later use
            useSolidColor: texture === null,
            isTransitioning: false,
            transitionProgress: 0,
            transitionStartTime: 0,
            labelMesh: labelPlane,
            labelTexture: labelTexture
        });

        // Make dome visible - it shows a tiny shimmering portal window at rest
        dome.visibility = 1;
        material.setFloat("isActive", 1); // Fully visible
        material.setFloat("basePortalSize", portalRestSize); // Portal at portal-specific rest size

        logger.debug(`Preview dome created for ${hotspot.id}`);
    }

    /**
     * Show portal preview for a specific hotspot (on hover)
     */
    showPreview(hotspotId: string): void {
        const preview = this.previewDomes.get(hotspotId);
        if (preview) {
            logger.debug(`Animating portal expansion for ${hotspotId}`);
            preview.targetPortalSize = preview.hoverSize;  // Expand to portal-specific hover size

            // Make dome visible when showing preview
            preview.mesh.visibility = 1;

            // Show label
            if (preview.labelMesh) {
                logger.debug(`Showing label for ${hotspotId}`);
                preview.labelMesh.visibility = 1;
            }
        } else {
            logger.warn(`No preview dome found for ${hotspotId}!`);
            logger.debug('Available preview IDs:', Array.from(this.previewDomes.keys()));
        }
    }

    /**
     * Hide portal preview for a specific hotspot (on hover exit)
     */
    hidePreview(hotspotId: string): void {
        const preview = this.previewDomes.get(hotspotId);
        if (preview) {
            logger.debug(`Animating portal shrink for ${hotspotId}`);
            preview.targetPortalSize = preview.restSize;  // Shrink back to portal-specific rest size
            
            // Hide label
            if (preview.labelMesh) {
                preview.labelMesh.visibility = 0;
            }
        }
    }

    /**
     * Hide all previews (instant, no animation)
     */
    hideAllPreviews(): void {
        this.previewDomes.forEach((preview) => {
            preview.targetAlpha = 0;
            preview.currentAlpha = 0;
            preview.isTransitioning = false;
            preview.transitionProgress = 0;
            preview.material.setFloat("isActive", 0);
            preview.material.setFloat("transitionProgress", 0);

            // Immediately hide the mesh (don't wait for shader)
            if (preview.mesh) {
                preview.mesh.visibility = 0;
            }

            // Hide label
            if (preview.labelMesh) {
                preview.labelMesh.visibility = 0;
            }
        });

        logger.debug(`Hidden ${this.previewDomes.size} portal previews immediately`);
    }

    /**
     * Start portal expansion transition for teleporting
     * @param hotspotId The hotspot ID to expand from
     * @returns The target stage ID, or null if not found
     */
    startTransition(hotspotId: string): string | null {
        const preview = this.previewDomes.get(hotspotId);
        if (preview) {
            logger.debug(`Starting transition animation for ${hotspotId}`);

            // Set to fully visible and start transition
            preview.currentAlpha = 1;
            preview.targetAlpha = 1;
            preview.isTransitioning = true;
            preview.transitionProgress = 0;
            preview.transitionStartTime = performance.now();
            preview.material.setFloat("isActive", 1);

            // Make dome visible (texture should already be loaded from hotspot initialization)
            preview.mesh.visibility = 1;

            // Hide label immediately when transition starts
            if (preview.labelMesh) {
                preview.labelMesh.visibility = 0;
            }

            return preview.targetStageId;
        }

        logger.warn(`Cannot start transition - no preview for ${hotspotId}`);
        return null;
    }

    /**
     * Start portal expansion from center of view for UI/VR button transitions
     * @param targetStage The stage to transition to
     * @param onComplete Callback when transition finishes
     */
    async startCenterTransition(targetStage: ExperienceStage, onComplete: () => void): Promise<void> {
        logger.debug(`Starting center portal transition to stage ${targetStage.id}`);
        logger.debug(`Target skybox type: ${targetStage.skybox?.type}, URL: ${targetStage.skybox?.url}`);

        // Get camera forward direction (where user is looking)
        const camera = this.scene.activeCamera;
        if (!camera) {
            logger.warn('No active camera, falling back to immediate transition');
            onComplete();
            return;
        }

        // Get camera's EXACT forward direction
        const forward = camera.getForwardRay().direction.normalize();

        // For center transitions, position portal in camera forward direction
        // Must be far enough to not interfere with near plane
        const centerPosition = forward.scale(100);

        logger.debug('Camera forward:', forward);
        logger.debug('Camera position:', camera.position);
        logger.debug('Portal position:', centerPosition);

        // Create temporary "hotspot" ID for this transition
        const tempId = `_center_transition_${Date.now()}`;
        this.lastCenterTransitionId = tempId; // Store for texture transfer
        logger.debug(`Creating temp preview dome with ID: ${tempId}`);

        // Build temp preview using same dome creation logic
        await this.createTempPreviewDome(tempId, centerPosition, targetStage);

        // Start transition immediately
        const preview = this.previewDomes.get(tempId);
        logger.debug('Preview dome created:', preview !== undefined);
        logger.debug('Preview has texture:', preview?.texture !== undefined && preview?.texture !== null);
        logger.debug('Total preview domes now:', this.previewDomes.size);

        if (preview) {
            logger.debug('Starting transition animation...');

            // Ensure texture or solid color is set before showing
            if (!preview.texture && !preview.useSolidColor) {
                logger.warn('No texture or solid color set, skipping transition');
                onComplete();
                return;
            }

            // Start with alpha at 0 and fade in quickly to avoid white flash
            preview.currentAlpha = 0;
            preview.targetAlpha = 1;
            preview.isTransitioning = true;
            preview.transitionProgress = 0;
            preview.transitionStartTime = performance.now();

            // Make dome visible (shader starts at alpha 0)
            preview.mesh.visibility = 1;
            logger.debug('Dome made visible, starting fade-in then transition');

            // Set callback
            this.onTransitionComplete = () => {
                logger.debug('Center transition complete, loading stage');
                onComplete();
            };
        } else {
            logger.error('Failed to create center preview dome!');
            logger.debug('Falling back to immediate transition');
            onComplete();
        }
    }

    /**
     * Create temporary preview dome for center transitions
     */
    private async createTempPreviewDome(id: string, position: Vector3, targetStage: ExperienceStage): Promise<void> {
        logger.debug(`Creating temp preview dome for ${id}`);
        logger.debug('Target stage:', targetStage.id);
        logger.debug('Has skybox:', !!targetStage.skybox);

        if (!targetStage.skybox) {
            logger.warn('No skybox on target stage, cannot create preview');
            return;
        }

        logger.debug(`Skybox type: ${targetStage.skybox.type}, URL: ${targetStage.skybox.url}`);

        const dome = CreateSphere(`portalPreview_${id}`, {
            diameter: this.DOME_RADIUS * 2,
            segments: 64,
            sideOrientation: Mesh.BACKSIDE,
        }, this.scene);

        dome.infiniteDistance = true;

        // Match main dome rotation exactly (DomeComponent.ts:74)
        dome.rotation.y = Math.PI;
        // NO X rotation - main dome doesn't have it

        // Apply target stage's skybox rotation if configured
        if (targetStage.skybox.rotation !== undefined) {
            const targetRotationRadians = (targetStage.skybox.rotation * Math.PI) / 180;
            dome.rotation.y += targetRotationRadians;
            logger.debug(`Center transition: Applied target rotation ${targetStage.skybox.rotation}° to preview dome`);
        }

        // NO COMPENSATION - Preview shows target scene at its actual configured rotation
        // This ensures transferred rotation matches what portals expect in the new scene

        dome.renderingGroupId = 1;
        
        // Disable picking so it doesn't block hotspot interactions
        dome.isPickable = false;

        // CRITICAL: Hide dome IMMEDIATELY after creation to prevent white flash
        dome.visibility = 0;

        const material = new ShaderMaterial(`portalPreviewMaterial_${id}`, this.scene, {
            vertex: "portalPreview",
            fragment: "portalPreview",
        }, {
            attributes: ["position", "normal", "uv"],
            uniforms: ["world", "worldViewProjection", "hotspotPosition", "revealRadius", "edgeSoftness", "isActive", "useTexture", "solidColor", "time", "transitionProgress", "basePortalSize", "effectStyle", "glowColor", "edgeOpacity"],
            samplers: ["textureSampler"],
        });

        material.setVector3("hotspotPosition", position);
        material.setFloat("revealRadius", this.REVEAL_RADIUS);
        material.setFloat("edgeSoftness", this.EDGE_SOFTNESS);
        material.setFloat("isActive", 0);
        material.setFloat("time", 0);
        material.setFloat("transitionProgress", 0);
        material.setFloat("basePortalSize", 0.9999); // Start from tiny dot for smooth expansion
        material.setFloat("effectStyle", this.getStyleValue(this.portalEffectStyle));
        // Use configured glow color for center transitions
        material.setVector3("glowColor", this.parseColor(this.portalGlowColor));
        // Use configured edge opacity for center transitions
        material.setFloat("edgeOpacity", this.portalEdgeOpacity);
        material.transparencyMode = 3;
        material.backFaceCulling = false;
        material.disableDepthWrite = true;

        let texture: Texture | VideoTexture | null = null;
        const skyboxConfig = targetStage.skybox;

        if (skyboxConfig.type === 'color') {
            const colorHex = skyboxConfig.color || '#000000';
            const color = this.parseColor(colorHex);
            material.setVector3("solidColor", color);
            material.setFloat("useTexture", 0);
        } else if (skyboxConfig.type === 'image') {
            // Use DomeComponent's loadInBackground which properly handles multi-variant textures
            try {
                const mediaItem = {
                    id: `${id}-skybox`,
                    title: targetStage.id,
                    url: skyboxConfig.url || '' // MediaLoader will use variants if available
                };

                // Get DomeComponent from SceneManager to reuse its texture loading logic
                const domeComponent = this.sceneManager.getDomeComponent();
                texture = await domeComponent.loadInBackground(
                    mediaItem,
                    'image',
                    undefined, // No progress callback
                    skyboxConfig // Pass full skybox config for variant selection
                );

                logger.debug(`Center transition image loaded for ${id}`);

                material.setTexture("textureSampler", texture);
                material.setFloat("useTexture", 1);
            } catch (error) {
                logger.error(`Image failed to load for ${id}:`, error);
                // Fall back to solid color
                const color = this.parseColor('#000000');
                material.setVector3("solidColor", color);
                material.setFloat("useTexture", 0);
            }
        } else if (skyboxConfig.type === 'video') {
            // For video skyboxes, use thumbnail if available (better performance)
            if (skyboxConfig.thumbnailUrl) {
                logger.debug('Using video thumbnail for center transition:', skyboxConfig.thumbnailUrl);
                try {
                    const domeComponent = this.sceneManager.getDomeComponent();
                    const mediaItem = {
                        id: `${id}-thumbnail`,
                        title: targetStage.id,
                        url: skyboxConfig.thumbnailUrl
                    };
                    texture = await domeComponent.loadInBackground(mediaItem, 'image');

                    material.setTexture("textureSampler", texture);
                    material.setFloat("useTexture", 1);
                    logger.debug('Video thumbnail texture set for center transition');
                } catch (error) {
                    logger.error('Thumbnail load failed, falling back to black');
                    const color = this.parseColor('#000000');
                    material.setVector3("solidColor", color);
                    material.setFloat("useTexture", 0);
                }
            } else {
                // No thumbnail - show black (videos are too heavy for portal previews)
                logger.debug('No thumbnail for video, using black');
                const color = this.parseColor('#000000');
                material.setVector3("solidColor", color);
                material.setFloat("useTexture", 0);
            }
        }

        // Store BEFORE assigning material to avoid flash
        this.previewDomes.set(id, {
            mesh: dome,
            material: material,
            texture: texture,
            targetStageId: targetStage.id,
            hotspotPosition: position,
            hotspotConfig: null, // Center transitions don't have a hotspot config
            currentAlpha: 0,
            targetAlpha: 0,
            currentPortalSize: 0.9999,  // Center transitions don't animate size
            targetPortalSize: 0.9999,
            restSize: 0.9999,    // Not used for center transitions
            hoverSize: 0.9999,   // Not used for center transitions
            useSolidColor: texture === null,
            isTransitioning: false,
            transitionProgress: 0,
            transitionStartTime: 0,
        });

        // Set material LAST and keep invisible until we explicitly show it
        dome.material = material;
        dome.visibility = 0; // Fully transparent until transition starts

        logger.debug('Temp dome created, staying invisible until transition starts');
    }

    /**
     * Update preview settings
     */
    updateSettings(revealRadius?: number, edgeSoftness?: number): void {
        if (revealRadius !== undefined) {
            this.previewDomes.forEach(preview => {
                preview.material.setFloat("revealRadius", revealRadius);
            });
        }
        if (edgeSoftness !== undefined) {
            this.previewDomes.forEach(preview => {
                preview.material.setFloat("edgeSoftness", edgeSoftness);
            });
        }
    }

    /**
     * Parse hex color string to Vector3 (RGB 0-1)
     */
    private parseColor(hex: string): Vector3 {
        // Remove # if present
        hex = hex.replace('#', '');

        const r = parseInt(hex.substring(0, 2), 16) / 255;
        const g = parseInt(hex.substring(2, 4), 16) / 255;
        const b = parseInt(hex.substring(4, 6), 16) / 255;

        return new Vector3(r, g, b);
    }

    /**
     * Transfer preview dome texture to main dome for seamless transition
     * @param hotspotId The hotspot/temp ID that was used for transition (optional - will use last center transition if not provided)
     * @returns The texture and type to apply to main dome, or null
     */
    transferTexture(hotspotId?: string): { texture: Texture | VideoTexture, type: 'image' | 'video', rotation: number } | null {
        // If no ID provided, try to use the last center transition
        const idToUse = hotspotId || this.lastCenterTransitionId;

        if (!idToUse) {
            logger.warn('No hotspot ID and no last center transition ID!');
            return null;
        }

        const preview = this.previewDomes.get(idToUse);
        if (preview && preview.texture) {
            logger.debug(`Transferring texture from preview ${idToUse} to main dome`);

            const texture = preview.texture;
            const type = texture instanceof VideoTexture ? 'video' : 'image';
            // Transfer the preview dome's WORLD rotation to maintain visual continuity
            let rotation = preview.mesh.rotation.y;

            // Only add parent rotation if this dome is actually parented to parentNode
            // Center transitions (UI arrows) are NOT parented, hotspot portals ARE parented
            if (this.parentNode && preview.mesh.parent === this.parentNode) {
                // Hotspot portal: local rotation includes counter-rotation, add parent rotation back
                rotation += this.parentNode.rotation.y;
                logger.debug(`Hotspot portal - converting local to world rotation`);
            }

            logger.debug(`Preview dome world rotation: ${rotation} radians (${(rotation * 180 / Math.PI).toFixed(1)}°)`);

            // Don't dispose the texture - we're transferring ownership to main dome
            preview.texture = null;

            // Clear the last ID since we've transferred it
            if (idToUse === this.lastCenterTransitionId) {
                this.lastCenterTransitionId = null;
            }

            return { texture, type, rotation };
        }

        logger.warn(`No texture to transfer for ID: ${idToUse}`);
        logger.debug('Available IDs:', Array.from(this.previewDomes.keys()));
        return null;
    }

    /**
     * Dispose only the temporary center transition preview
     */
    disposeCenterTransition(): void {
        if (!this.lastCenterTransitionId) {
            logger.debug('No center transition to dispose');
            return;
        }

        const preview = this.previewDomes.get(this.lastCenterTransitionId);
        if (preview) {
            logger.debug(`Disposing center transition preview: ${this.lastCenterTransitionId}`);

            // Dispose meshes and materials
            if (preview.material) preview.material.dispose();
            if (preview.mesh) preview.mesh.dispose();
            if (preview.labelTexture) preview.labelTexture.dispose();
            if (preview.labelMesh) preview.labelMesh.dispose();

            // Remove from map
            this.previewDomes.delete(this.lastCenterTransitionId);
            this.lastCenterTransitionId = null;

            logger.debug(`Center transition preview disposed. Remaining previews: ${this.previewDomes.size}`);
        }
    }

    /**
     * Dispose all preview domes
     */
    dispose(): void {
        logger.debug(`Disposing ${this.previewDomes.size} preview domes`);

        this.previewDomes.forEach(preview => {
            // Material
            if (preview.material) {
                preview.material.dispose();
            }
            // Mesh
            if (preview.mesh) {
                preview.mesh.dispose();
            }
            // Label
            if (preview.labelTexture) {
                preview.labelTexture.dispose();
            }
            if (preview.labelMesh) {
                preview.labelMesh.dispose();
            }
            // Texture is managed by SceneManager cache, so we generally don't dispose it here
            // unless we are sure we own it. For now, let GC handle it or SceneManager.
        });

        this.previewDomes.clear();
        logger.debug('Dispose complete - map cleared but animation observer still running');
    }

    /**
     * Convert portal style name to shader uniform value
     */
    private getStyleValue(style: PortalEffectStyle): number {
        switch (style) {
            case 'clean': return 0.0;
            case 'enhanced': return 1.0;
            case 'minimal': return 2.0;
            case 'alien': return 3.0;
            default: return 1.0; // Default to enhanced
        }
    }

    /**
     * Set portal effect style
     * @param style The portal effect style to use
     */
    setPortalEffectStyle(style: PortalEffectStyle): void {
        logger.debug(`Setting portal effect style to: ${style}`);
        this.portalEffectStyle = style;
        const styleValue = this.getStyleValue(style);

        // Update all existing portal materials
        this.previewDomes.forEach((preview) => {
            preview.material.setFloat("effectStyle", styleValue);
        });
    }

    /**
     * Get current portal effect style
     */
    getPortalEffectStyle(): PortalEffectStyle {
        return this.portalEffectStyle;
    }

    /**
     * Convert user-friendly scale to internal shader threshold
     * @param scale User scale (1.0 = default, 2.0 = 2x larger, 0.5 = half size)
     * @param defaultThreshold Default dot product threshold
     * @returns Internal shader threshold value
     */
    private scaleToThreshold(scale: number, defaultThreshold: number): number {
        // Convert threshold to angular radius
        const defaultAngle = Math.acos(defaultThreshold);

        // Scale the angle
        const scaledAngle = defaultAngle * scale;

        // Clamp angle to reasonable range (0.01° to 45°)
        const clampedAngle = Math.max(0.01 * Math.PI / 180, Math.min(45 * Math.PI / 180, scaledAngle));

        // Convert back to threshold
        const threshold = Math.cos(clampedAngle);

        return threshold;
    }

    /**
     * Set portal rest size (size when not hovered)
     * @param scale Portal size scale (1.0 = default, 2.0 = 2x larger, 0.5 = half size)
     */
    setPortalRestSize(scale: number): void {
        this.portalRestSizeScale = Math.max(0.1, Math.min(5.0, scale)); // Clamp to reasonable range
        this.portalRestSize = this.scaleToThreshold(this.portalRestSizeScale, 0.9996);

        logger.debug(`Portal rest size scale: ${this.portalRestSizeScale}x (threshold: ${this.portalRestSize.toFixed(4)})`);

        // Update existing portals that are at rest
        this.previewDomes.forEach((preview) => {
            if (!preview.isTransitioning && preview.targetPortalSize > 0.9995) {
                preview.currentPortalSize = this.portalRestSize;
                preview.targetPortalSize = this.portalRestSize;
            }
        });
    }

    /**
     * Set portal hover size (size when hovered/expanded)
     * @param scale Portal size scale (1.0 = default, 2.0 = 2x larger, 0.5 = half size)
     */
    setPortalHoverSize(scale: number): void {
        this.portalHoverSizeScale = Math.max(0.1, Math.min(5.0, scale)); // Clamp to reasonable range
        this.portalHoverSize = this.scaleToThreshold(this.portalHoverSizeScale, 0.995);

        logger.debug(`Portal hover size scale: ${this.portalHoverSizeScale}x (threshold: ${this.portalHoverSize.toFixed(4)})`);
    }

    /**
     * Set portal edge opacity (leading edge glow visibility)
     * @param opacity Edge opacity (0 = transparent, 1 = fully visible)
     */
    setPortalEdgeOpacity(opacity: number): void {
        this.portalEdgeOpacity = Math.max(0, Math.min(1.0, opacity)); // Clamp to 0-1
        logger.debug(`Portal edge opacity: ${this.portalEdgeOpacity}`);

        // Update all existing portal materials
        this.previewDomes.forEach((preview) => {
            preview.material.setFloat("edgeOpacity", this.portalEdgeOpacity);
        });
    }

    /**
     * Get current portal edge opacity
     */
    getPortalEdgeOpacity(): number {
        return this.portalEdgeOpacity;
    }

    /**
     * Set portal glow color (leading edge color)
     * @param color Hex color string (e.g., '#00bfff')
     */
    setPortalGlowColor(color: string): void {
        this.portalGlowColor = color;
        logger.debug(`Portal glow color: ${this.portalGlowColor}`);

        // Update all existing portal materials
        const colorVec = this.parseColor(color);
        this.previewDomes.forEach((preview) => {
            preview.material.setVector3("glowColor", colorVec);
        });
    }

    /**
     * Get current portal glow color
     */
    getPortalGlowColor(): string {
        return this.portalGlowColor;
    }

    /**
     * Apply portal configuration from experience config
     * @param config Portal configuration object
     */
    applyPortalConfig(config?: { effectStyle?: PortalEffectStyle; restSize?: number; hoverSize?: number; edgeOpacity?: number; glowColor?: string }): void {
        if (!config) {
            logger.debug('No portal config provided, using defaults');
            return;
        }

        logger.debug('Applying portal configuration:', config);

        if (config.effectStyle) {
            this.setPortalEffectStyle(config.effectStyle);
        }

        if (config.restSize !== undefined) {
            this.setPortalRestSize(config.restSize);
        }

        if (config.hoverSize !== undefined) {
            this.setPortalHoverSize(config.hoverSize);
        }

        if (config.edgeOpacity !== undefined) {
            this.setPortalEdgeOpacity(config.edgeOpacity);
        }

        if (config.glowColor !== undefined) {
            this.setPortalGlowColor(config.glowColor);
        }
    }

    /**
     * Get current portal sizes (user-friendly scale values)
     */
    getPortalSizes(): { restSize: number, hoverSize: number } {
        return {
            restSize: this.portalRestSizeScale,
            hoverSize: this.portalHoverSizeScale
        };
    }

    /**
     * Show gaze portal (follows camera direction for free-look navigation)
     */
    async showGazePortal(targetStage: ExperienceStage): Promise<void> {
        // Dispose existing gaze portal if any
        this.hideGazePortal();

        logger.debug('Creating gaze portal for stage:', targetStage.id);

        // Create gaze portal at camera forward position (will be updated each frame)
        const camera = this.scene.activeCamera;
        if (!camera) return;

        const forward = camera.getForwardRay().direction;
        const cameraPos = camera.position;
        const gazePosition = cameraPos.add(forward.scale(100)); // 100 units ahead

        // Create portal using center transition logic (reuses texture loading)
        await this.createTempPreviewDome('_gaze_portal', gazePosition, targetStage);

        // Get the created dome
        const preview = this.previewDomes.get('_gaze_portal');
        if (preview) {
            this.gazePortalDome = preview;
            preview.mesh.visibility = 1;
            preview.material.setFloat('isActive', 1);
            preview.currentPortalSize = this.portalHoverSize; // Show at hover size
            preview.targetPortalSize = this.portalHoverSize;
            preview.material.setFloat('basePortalSize', this.portalHoverSize);

            logger.debug('Gaze portal created and visible');
        }
    }

    /**
     * Hide gaze portal
     */
    hideGazePortal(): void {
        if (this.gazePortalDome) {
            logger.debug('Hiding gaze portal');

            // Dispose the gaze portal
            this.gazePortalDome.mesh.dispose();
            this.gazePortalDome.material.dispose();
            if (this.gazePortalDome.texture) {
                this.gazePortalDome.texture.dispose();
            }

            this.previewDomes.delete('_gaze_portal');
            this.gazePortalDome = null;
        }
    }

    /**
     * Update gaze portal position to follow camera
     */
    updateGazePortalPosition(): void {
        if (!this.gazePortalDome) return;

        const camera = this.scene.activeCamera;
        if (!camera) return;

        // Get camera forward direction
        const forward = camera.getForwardRay().direction;
        const cameraPos = camera.position;

        // Position portal 100 units ahead in look direction
        const gazePosition = cameraPos.add(forward.scale(100));

        // Update hotspot position for shader
        this.gazePortalDome.material.setVector3('hotspotPosition', gazePosition);
        this.gazePortalDome.hotspotPosition = gazePosition;
    }

    /**
     * Hide all regular portals
     */
    hideAllPortals(): void {
        this.previewDomes.forEach((preview, id) => {
            if (id !== '_gaze_portal') {
                preview.mesh.visibility = 0;
                if (preview.labelMesh) {
                    preview.labelMesh.visibility = 0;
                }
            }
        });
    }

    /**
     * Show all regular portals
     */
    showAllPortals(): void {
        this.previewDomes.forEach((preview, id) => {
            if (id !== '_gaze_portal') {
                preview.mesh.visibility = 1;
                // Labels visibility is controlled by hover state
            }
        });
    }

    /**
     * Adjust gaze portal transition progress manually (scroll/thumbstick)
     */
    adjustGazePortalProgress(delta: number): void {
        if (!this.gazePortalDome) return;

        // Adjust progress manually
        this.gazePortalDome.transitionProgress = Math.max(0, Math.min(1.0, this.gazePortalDome.transitionProgress + delta));

        // Update shader immediately
        this.gazePortalDome.material.setFloat("transitionProgress", this.gazePortalDome.transitionProgress);

        logger.debug(`Portal progress: ${(this.gazePortalDome.transitionProgress * 100).toFixed(0)}%`);
    }

    /**
     * Expand gaze portal to full transition
     */
    async expandGazePortalToTransition(targetStage: ExperienceStage, onComplete: () => Promise<void>): Promise<void> {
        if (!this.gazePortalDome) {
            logger.warn('No gaze portal to expand!');
            return;
        }

        const currentProgress = this.gazePortalDome.transitionProgress;
        logger.debug(`Starting gaze portal expansion from ${(currentProgress * 100).toFixed(0)}% to stage:`, targetStage.id);

        // Update target stage ID for callback
        this.gazePortalDome.targetStageId = targetStage.id;

        // Reset portal to hover size before starting transition
        this.gazePortalDome.currentPortalSize = this.portalHoverSize;
        this.gazePortalDome.targetPortalSize = this.portalHoverSize;
        this.gazePortalDome.material.setFloat('basePortalSize', this.portalHoverSize);

        // Mark as transitioning (animation loop will expand it)
        this.gazePortalDome.isTransitioning = true;
        // DON'T reset progress - continue from current scroll position
        // Calculate fake start time based on current progress to maintain smooth easing
        const easedT = Math.min(currentProgress / 1.2, 0.999); // Clamp to avoid acos domain issues
        const t = Math.acos(1 - 2 * easedT) / Math.PI; // Reverse the easing function
        this.gazePortalDome.transitionStartTime = performance.now() - (t * this.TRANSITION_DURATION * 1000);

        // Make sure it's visible and active
        this.gazePortalDome.mesh.visibility = 1;
        this.gazePortalDome.material.setFloat('isActive', 1);
        this.gazePortalDome.currentAlpha = 1;
        this.gazePortalDome.targetAlpha = 1;

        // Update last transition ID for texture transfer
        this.lastCenterTransitionId = '_gaze_portal';

        // Set callback
        this.onTransitionComplete = async (_targetStageId: string) => {
            logger.debug('Gaze portal expansion complete, executing callback');
            await onComplete();
        };

        // The animation loop will handle the expansion
        logger.debug('Gaze portal expansion initiated - animation loop will expand from hover size to full sphere');
    }
}



